import React from "react";
import PropTypes from "prop-types";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import {
  get,
  map,
  forEach,
  filter,
  size,
  sum,
  includes,
  indexOf,
  sortBy,
  concat,
} from "lodash";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListSubheader from "@material-ui/core/ListSubheader";
import Autocomplete from "@material-ui/lab/Autocomplete";

import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";

import colors from "../../assets/style/colors";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const styles = {
  textField: {
    margin: 0,
    backgroundColor: "white",
  },
  selectMenu: {
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    fontSize: "0.875em",
  },
  menuList: {
    padding: "8px 0px",
    //maxHeight: "500px",
  },
  placeholder: {
    fontStyle: "italic",
    colors: colors.black54,
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    fontSize: "0.875em",
  },
  input: {
    height: "36px",
  },
  selectInput: {
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    fontSize: "0.875em",
  },
  menuItem: {
    fontSize: "1em",
    height: "48px",
    padding: "0px 24px 0px 18px",
  },
  menuItemSelectAll: {
    fontSize: "1em",
    color: colors.primary,
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: colors.white,
    },
  },
  listSubheader: {
    lineHeight: "30px",
    backgroundColor: colors.cardDarkBg,
  },
  checkboxRoot: {
    padding: "4px",
  },
  autoCompleteRoot: {
    height: "36px",
    fontSize: "1em",
    padding: "8px 35px 8px 11px !important",
  },
  autoCompletePaper: {
    fontSize: "1em",
    padding: 0,
    margin: 0,
    minWidth: "250px",
  },
  textFieldRoot: {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: colors.primary,
        borderWidth: "1px",
      },
    },
  },
};

const CustomSelect = (props) => {
  const { classes } = props;
  const [open, _setOpen] = React.useState(false);
  const openRef = React.useRef(open);

  const setOpen = (value) => {
    setTimeout(() => {
      openRef.current = value;
      _setOpen(value);
      if (value && props.clickThrough) {
        setPointerEventsNone();
      }
    }, 200);
  };

  const onOpen = (e) => {
    setOpen(true);
  };

  const onClose = (e) => {
    if (props.onClose) {
      props.onClose(e);
    }
    setOpen(false);
  };

  // Enable to click through the popover
  const setPointerEventsNone = () => {
    let popover = get(document.getElementsByClassName("MuiPopover-root"), [0]);
    if (popover !== undefined) {
      popover.style.pointerEvents = "none";
    }
  };

  React.useEffect(() => {
    // Listen click outside the menu to close the dropdown
    window.addEventListener("click", (event) => {
      let menu = get(document.getElementsByClassName("MuiMenu-paper"), [0]);
      let insideMenu = false;
      if (
        menu &&
        event.clientX > menu.offsetLeft &&
        event.clientX < menu.offsetLeft + menu.offsetWidth &&
        event.clientY > menu.offsetTop &&
        event.clientY < menu.offsetTop + menu.offsetHeight
      ) {
        insideMenu = true;
      }
      if (openRef.current && !insideMenu) {
        //setOpen(false);
        onClose();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectAllValue = () => {
    if (props.categories) {
      if (
        sum(map(get(props, ["options"]), (opt) => size(opt.options))) ===
        size(props.value)
      ) {
        return "selectNone";
      }
      return "selectAll";
    } else {
      if (size(get(props, ["options"])) === size(props.value)) {
        return "selectNone";
      }
      return "selectAll";
    }
  };

  const selectAllString = () => {
    if (props.categories) {
      if (
        sum(map(get(props, ["options"]), (opt) => size(opt.options))) ===
        size(props.value)
      ) {
        return I18n.get("profiles_deselectAll");
      }
      return I18n.get("profiles_selectAll");
    } else {
      if (size(get(props, ["options"])) === size(props.value)) {
        return I18n.get("profiles_deselectAll");
      }
      return I18n.get("profiles_selectAll");
    }
  };

  const isCategoryChecked = (cat) => {
    let res = true;
    forEach(get(cat, ["options"]), (opt) => {
      if (!includes(props.value, opt.id)) {
        res = false;
      }
    });
    return res;
  };

  const selectAllCategory = (e, cat) => {
    e.stopPropagation();
    let newValues = [...props.value];
    if (isCategoryChecked(cat)) {
      forEach(get(cat, ["options"]), (o) => {
        if (includes(newValues, o.id)) {
          let index = indexOf(newValues, o.id);
          newValues.splice(index, 1);
        }
      });
    } else {
      forEach(get(cat, ["options"]), (o) => {
        if (!includes(newValues, o.id)) {
          newValues.push(o.id);
        }
      });
    }
    props.onChange({
      target: {
        value: newValues,
      },
    });
  };

  let categories = [];
  if (props.categories) {
    forEach(get(props, ["options"]), (opt) => {
      if (!opt.noCategory) {
        categories.push(
          <ListSubheader
            key={opt.id}
            color="primary"
            component="div"
            classes={{ root: classes.listSubheader }}
          >
            <Grid container alignItems="center">
              <Grid item>
                {props.multiple && (
                  <Checkbox
                    color="primary"
                    checked={isCategoryChecked(opt)}
                    classes={{ root: classes.checkboxRoot }}
                    onClick={(e) => selectAllCategory(e, opt)}
                  />
                )}
              </Grid>
              <Grid item xs className="text-center">
                {opt.name}
              </Grid>
            </Grid>
          </ListSubheader>
        );
      }
      forEach(get(opt, ["options"]), (item) => {
        categories.push(
          <MenuItem
            key={`${opt.id}${item.id}`}
            value={item.id}
            style={{ fontSize: "1em" }}
            //onClick={() => handleSelect(item.id)}
          >
            {props.multiple && (
              <Checkbox
                color="primary"
                checked={props.value.indexOf(item.id) > -1}
                classes={{ root: classes.checkboxRoot }}
              />
            )}
            {item.name}
          </MenuItem>
        );
      });
    });
  }
  if (props.comboBox) {
    return (
      <Autocomplete
        id={props.name}
        disabled={props.disabled}
        options={concat(
          props.multiple
            ? [{ id: selectAllValue(), name: selectAllString() }]
            : [],
          get(props, ["noSort"])
            ? get(props, ["options"])
            : sortBy(get(props, ["options"]), (opt) => opt.name)
        )}
        getOptionLabel={(option) =>
          get(option, ["name"]) ||
          get(
            filter(
              get(props, ["options"]),
              (opt) => get(opt, ["id"]) === parseInt(option)
            ),
            [0, "name"]
          ) ||
          ""
        }
        classes={{
          inputRoot: classes.autoCompleteRoot,
          paper: classes.autoCompletePaper,
        }}
        disableCloseOnSelect={props.multiple}
        multiple={props.multiple}
        getOptionSelected={(option, value) => get(option, ["id"]) === value}
        autoHighlight
        noOptionsText={I18n.get("common_noOptions")}
        value={props.value || null}
        renderOption={(option, { selected }) => (
          <React.Fragment>
            {props.multiple && option.id !== selectAllValue() && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
                color="primary"
              />
            )}
            {option.id === selectAllValue() ? (
              <span
                className={classes.menuItemSelectAll}
                style={{ backgroundColor: "unset" }}
              >
                {option.name}
              </span>
            ) : (
              option.name
            )}
          </React.Fragment>
        )}
        onChange={(e, v) => {
          if (props.multiple) {
            e.stopPropagation();
            props.onChange({
              target: {
                value: map(v, (i) => get(i, ["id"]) || i),
              },
            });
          } else {
            props.onChange({
              target: {
                value:
                  get(v, ["id"]) || get(v, ["id"]) === 0
                    ? get(v, ["id"])
                    : null,
              },
            });
          }
        }}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        disableClearable={props.multiple}
        //renderTags={props.renderValue}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            error={props.error || false}
            classes={{
              root: classes.textField,
            }}
            variant="outlined"
            margin="dense"
            SelectProps={{
              multiple: props.multiple,
            }}
          />
        )}
      />
    );
  }

  return (
    <TextField
      name={props.name}
      error={props.error || false}
      className={classes.textField}
      classes={{ root: classes.textFieldRoot }}
      select
      fullWidth={!props.disableFullWidth}
      value={props.value}
      onChange={props.onChange}
      variant="outlined"
      margin="dense"
      data-automation={props.dataAutomation}
      disabled={props.disabled}
      InputProps={{
        className: classes.input,
      }}
      SelectProps={{
        onBlur: props.onBlur,
        classes: {
          root: !props.value ? classes.placeholder : classes.selectInput,
          select: classes.select,
        },
        MenuProps: {
          autoFocus: false,
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          MenuListProps: {
            classes: { root: classes.menuList },
            id: "selectMenuList",
          },
        },
        open: props.clickThrough ? open : null,
        onOpen: props.clickThrough ? onOpen : null,
        onClose: props.clickThrough ? onClose : null,
        displayEmpty: true,
        renderValue: props.renderValue
          ? (selected) => {
              if (props.color) {
                return (
                  <Box
                    style={{
                      width: "100px",
                      height: "20px",
                      borderRadius: "1px",
                      backgroundColor: `#${get(
                        filter(
                          get(props, ["options"]),
                          (opt) => opt.id === selected
                        ),
                        [0, "name"]
                      )}`,
                    }}
                  />
                );
              }
              if (
                (size(selected) === 0 && props.nullForAll) ||
                (size(selected) > 1 &&
                  ((!props.categories &&
                    size(selected) === size(get(props, ["options"]))) ||
                    sum(
                      map(get(props, ["options"]), (opt) => size(opt.options))
                    ) === size(selected)))
              ) {
                return I18n.get("common_all");
              }
              return props.renderValue(selected);
            }
          : null,
        multiple: props.multiple,
      }}
    >
      {props.multiple && props.selectAll && (
        <MenuItem
          classes={{ root: classes.menuItemSelectAll }}
          value={selectAllValue()}
        >
          {selectAllString()}
        </MenuItem>
      )}
      {props.categories
        ? categories
        : map(
            get(props, ["noSort"])
              ? get(props, ["options"])
              : sortBy(get(props, ["options"]), (opt) => opt.name),
            (opt) => (
              <MenuItem
                key={opt.id}
                value={opt.id}
                classes={{ root: classes.menuItem }}
              >
                {props.multiple && (
                  <Checkbox
                    color="primary"
                    classes={{ root: classes.checkboxRoot }}
                    checked={props.value.indexOf(opt.id) > -1}
                  />
                )}
                {props.color ? (
                  <Box
                    style={{
                      width: "100px",
                      height: "20px",
                      borderRadius: "1px",
                      backgroundColor: `#${opt.name}`,
                    }}
                  />
                ) : (
                  opt.name
                )}
              </MenuItem>
            )
          )}
    </TextField>
  );
};

CustomSelect.propTypes = {
  error: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any,
      name: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  renderValue: PropTypes.func,
  multiple: PropTypes.bool,
  categories: PropTypes.bool,
};

export default withStyles(styles)(CustomSelect);
